import type { TFunction } from "i18next";

import { submitNewBuilding, updateExistingBuiding } from "features/dt-basic-setup/single-building/api/pipeline";
import { buildAddBuildingStepUrl } from "features/dt-basic-setup/single-building/lib/urlState";
import { STEP } from "features/dt-basic-setup/single-building/model/constants";
import { isNumeric } from "utils/dataManipulation";
import { ROUTE_PATHS } from "utils/routerDom";

import type { HandleSubmitBase, SubmitAction, SubmitCaller } from "../types";

import { SingleBuildingFormState } from "../../../model/types";
import { REQUIRED_ADDRESS_FIELDS } from "../constants";

export const createSubmitAction = ({
  handleSubmitBase,
  isEpcExtractionAvailable,
  isSaveAndExit,
  projectId,
  t,
}: {
  handleSubmitBase: HandleSubmitBase;
  isEpcExtractionAvailable: boolean;
  isSaveAndExit: boolean;
  projectId: string | undefined;
  t: TFunction;
}): SubmitAction => {
  const isExisting = Boolean(projectId);

  const caller: SubmitCaller = isExisting
    ? (formData, dtCreation) => updateExistingBuiding(formData, dtCreation, projectId, isEpcExtractionAvailable)
    : submitNewBuilding;

  const getNavigateUrl = isSaveAndExit
    ? (id: string) => ROUTE_PATHS.buildingProfile(id).navigateUrl
    : (id: string) => buildAddBuildingStepUrl(id, STEP.final);

  return {
    action: () =>
      handleSubmitBase({
        caller,
        dtCreation: !isSaveAndExit,
        shouldDeleteImages: isExisting,
      }),
    getNavigateUrl,
    projectActionLabel: isExisting
      ? (t("AddBuilding.submitModal.rows.projectUpdateLabel") as string)
      : (t("AddBuilding.submitModal.rows.projectCreationLabel") as string),
  };
};

const areRequiredAddressFieldsFilled = (formData: SingleBuildingFormState) => {
  const hasRequiredAddressFields = REQUIRED_ADDRESS_FIELDS.every((key) => {
    const value = formData.addressInfo?.[key];
    return typeof value === "string" && value.trim().length > 0;
  });
  const hasSpecifiedBuildingCenter = isNumeric(formData.buildingCenter.lat) && isNumeric(formData.buildingCenter.lng);
  return hasRequiredAddressFields && hasSpecifiedBuildingCenter;
};

export const isFormFilled = (formData: SingleBuildingFormState, projectId: string | undefined) => {
  return projectId || areRequiredAddressFieldsFilled(formData);
};
